import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import * as echarts from "echarts";
import * as moment from "moment";
import { Subscription } from "rxjs";
import { APIService } from "src/app/api/api.service";
import { NotifierService } from "src/app/_services/notifier.service";
import { environment } from "src/environments/environment";

declare let flatpickr: any;

// Supported API actions for each support dashboard section.
type SupportDashboardAction =
  | "priority_summary"
  | "sla_summary"
  | "sla_priority_summary"
  | "operational_summary"
  | "mttr_summary"
  | "creation_time_summary";

type SupportDashboardDetailMetric =
  | "total-tickets"
  | "resolution-met"
  | "resolution-breached"
  | "response-met"
  | "response-breached"
  | "business-hours"
  | "after-hours"
  | "priority-details"
  | "sla-priority-details";

interface PercentageDisplay {
  value: number;
  display: string;
}

interface SupportDashboardRequestBody {
  action: SupportDashboardAction;
  jira_organization_key?: string;
  jira_project_key: string[];
  start_date: string;
  end_date: string;
  date_field: string;
  include_resolved: boolean;
  assignees?: string[];
}

type SupportDashboardRequestPayload = SupportDashboardRequestBody;

// Common response metadata shared by all dashboard endpoints.
interface DashboardDateRange {
  start_date: string;
  end_date: string;
  date_field: string;
}

interface DashboardResponseBase {
  dashboard_section: string;
  date_range: DashboardDateRange;
  status: string;
}

interface DashboardTableState<T extends DashboardResponseBase> {
  loading: boolean;
  errorMessage: string | null;
  requestPayload: SupportDashboardRequestPayload | null;
  responseBody: T | null;
}

interface JiraContext {
  jiraOrganizationKey?: string;
  jiraProjectKey: string;
  jiraProjectKeys: string[];
}

interface AssigneeOption {
  id: string;
  name: string;
  ticketCount: number;
}

interface OrganizationOption {
  id: string;
  name: string;
  ticketCount: number;
}

type AssigneeSelectionValue =
  | string
  | AssigneeOption
  | {
      id?: string;
      name?: string;
    };

type OrganizationSelectionValue =
  | string
  | OrganizationOption
  | {
      id?: string;
      name?: string;
    };

interface AssigneeListResponse {
  organization?: string;
  assignees?: Array<{
    assignee_name?: string;
    reporter_name?: string;
    name?: string;
    ticket_count: number;
  }>;
  total?: number;
  status?: string | number;
  s?: string | number;
  message?: string;
  error_message?: string;
}

interface OrganizationListResponse {
  organizations?: Array<{
    organization_name?: string;
    name?: string;
    ticket_count: number;
  }>;
  total?: number;
  status?: string | number;
  s?: string | number;
  message?: string;
  error_message?: string;
}

interface VolumeByPriorityRow {
  priority: string;
  count: number;
  percentage: PercentageDisplay;
}

interface VolumeByPriorityResponseBody extends DashboardResponseBase {
  summary: {
    total_tickets: number;
    rows: VolumeByPriorityRow[];
  };
}

interface SlaComplianceOverallRow {
  sla_type: string;
  met: number;
  breached: number;
  total: number;
  compliance_percentage: PercentageDisplay;
}

interface SlaComplianceOverallResponseBody extends DashboardResponseBase {
  summary: {
    rows: SlaComplianceOverallRow[];
  };
}

interface SlaComplianceByPriorityRow {
  priority: string;
  resolution_met: number;
  resolution_breached: number;
  resolution_percentage: PercentageDisplay;
  response_met: number;
  response_breached: number;
  response_percentage: PercentageDisplay;
}

interface SlaComplianceByPriorityTotal {
  resolution_met: number;
  resolution_breached: number;
  resolution_percentage: PercentageDisplay;
  response_met: number;
  response_breached: number;
  response_percentage: PercentageDisplay;
}

interface SlaComplianceByPriorityResponseBody extends DashboardResponseBase {
  summary: {
    rows: SlaComplianceByPriorityRow[];
    total: SlaComplianceByPriorityTotal;
  };
}

interface TicketsByPriorityRow {
  priority: string;
  new: number;
  in_progress: number;
  resolved: number;
}

interface TicketsByCreationTimeRow {
  time_period: string;
  ticket_count: number;
  percentage: PercentageDisplay;
}

interface SystemHealthSummary {
  overall_score: PercentageDisplay;
  label: string;
}

interface OperationalSummaryResponseBody extends DashboardResponseBase {
  summary: {
    tickets_by_creation_time: {
      total_tickets: number;
      rows: TicketsByCreationTimeRow[];
    };
    daily_sunrise_report: {
      rows: TicketsByPriorityRow[];
    };
    upcoming_change_schedules: {
      issues: any[];
      total: number;
      all_issues: number;
    };
    system_health: SystemHealthSummary;
  };
}

interface MttrByPriorityRow {
  priority: string;
  avg_resolution_time_hours: number | null;
  ticket_count: number;
}

interface MttrByPriorityOverall {
  avg_resolution_time_hours: number;
  ticket_count: number;
}

interface MttrByPriorityResponseBody extends DashboardResponseBase {
  summary: {
    rows: MttrByPriorityRow[];
    overall: MttrByPriorityOverall;
  };
}

interface TicketsByCreationTimeResponseBody extends DashboardResponseBase {
  summary: {
    total_tickets: number;
    rows: TicketsByCreationTimeRow[];
  };
}

// Creates the default loading/error/data shape for a dashboard table.
export interface SupportDashboardDownloadData {
  startDate: string;
  endDate: string;
  assigneeDisplay: string;
  totalTickets: number;
  resolutionSLA: string;
  responseSLA: string;
  avgMTTR: string;
  volumeData: VolumeByPriorityRow[];
  slaOverallData: SlaComplianceOverallRow[];
  slaPriorityData: SlaComplianceByPriorityRow[];
  slaPriorityTotal: SlaComplianceByPriorityTotal;
  mttrData: MttrByPriorityRow[];
  mttrOverall: MttrByPriorityOverall;
  creationTimeData: TicketsByCreationTimeRow[];
}

function createTableState<T extends DashboardResponseBase>(): DashboardTableState<T> {
  return {
    loading: false,
    errorMessage: null,
    requestPayload: null,
    responseBody: null,
  };
}

const EMPTY_PERCENTAGE: PercentageDisplay = {
  value: 0,
  display: "0.00%",
};

const EMPTY_SLA_PRIORITY_TOTAL: SlaComplianceByPriorityTotal = {
  resolution_met: 0,
  resolution_breached: 0,
  resolution_percentage: EMPTY_PERCENTAGE,
  response_met: 0,
  response_breached: 0,
  response_percentage: EMPTY_PERCENTAGE,
};

const EMPTY_MTTR_OVERALL: MttrByPriorityOverall = {
  avg_resolution_time_hours: 0,
  ticket_count: 0,
};

@Component({
  selector: "app-support-dashboard",
  templateUrl: "./support-dashboard.component.html",
  styleUrls: ["./support-dashboard.component.css"],
})
export class SupportDashboardComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  // Chart containers coming from the template.
  @ViewChild("volumeByPriorityChart")
  volumeByPriorityChart: ElementRef<HTMLDivElement>;
  @ViewChild("slaComplianceOverallChart")
  slaComplianceOverallChart: ElementRef<HTMLDivElement>;
  @ViewChild("slaComplianceByPriorityChart")
  slaComplianceByPriorityChart: ElementRef<HTMLDivElement>;
  @ViewChild("mttrByPriorityChart")
  mttrByPriorityChart: ElementRef<HTMLDivElement>;
  @ViewChild("ticketsByCreationTimeChart")
  ticketsByCreationTimeChart: ElementRef<HTMLDivElement>;

  // Page-level filters and account context.
  accountId = localStorage.getItem("accountId") || "";
  urlPrefix = localStorage.getItem("role") == "Admin" ? "admin" : "client";
  startDate = moment().startOf("month").format("YYYY-MM-DD");
  endDate = moment().format("YYYY-MM-DD");
  dateRange = "";
  dateField = "created";
  includeResolved = true;
  currentMessage: Subscription | null = null;
  supportDetailQueryParams: {
    startDate: string;
    endDate: string;
    dateField: string;
    includeResolved: boolean;
    organization?: string;
    assignees?: string;
  } = {
    startDate: this.startDate,
    endDate: this.endDate,
    dateField: this.dateField,
    includeResolved: this.includeResolved,
  };
  organizationOptions: OrganizationOption[] = [];
  selectedOrganization = "";
  loadingOrganizations = false;
  assigneeOptions: AssigneeOption[] = [];
  selectedAssignees: string[] = [];
  loadingAssignees = false;

  volumeByPriorityTableState = createTableState<VolumeByPriorityResponseBody>();
  slaComplianceOverallTableState =
    createTableState<SlaComplianceOverallResponseBody>();
  slaComplianceByPriorityTableState =
    createTableState<SlaComplianceByPriorityResponseBody>();
  ticketsByPriorityTableState = createTableState<OperationalSummaryResponseBody>();
  mttrByPriorityTableState = createTableState<MttrByPriorityResponseBody>();
  ticketsByCreationTimeTableState =
    createTableState<TicketsByCreationTimeResponseBody>();
  loadingSlaSummaryCards = true;

  // Shared chart instances and colors used across all widgets.
  private chartsReady = false;
  private chartRenderTimer: ReturnType<typeof setTimeout> | null = null;
  private isPreparingChartExport = false;
  private charts: { [key: string]: echarts.ECharts } = {};
  private readonly priorityColors: { [key: string]: string } = {
    critical: "#c00000",
    high: "#ed7d31",
    medium: "#ffc000",
    low: "#70ad47",
  };
  private readonly defaultChartColors = [
    "#4472c4",
    "#ed7d31",
    "#a5a5a5",
    "#ffc000",
    "#5b9bd5",
    "#70ad47",
  ];
  private readonly resolutionColor = "#ed7d31";
  private readonly responseColor = "#4472c4";
  private readonly metColor = "#4472c4";
  private readonly breachedColor = "#ed7d31";

  constructor(
    private apiService: APIService,
    private notifier: NotifierService,
    private route: ActivatedRoute
  ) {}

  // Set up listeners, date filter, and initial data load.
  ngOnInit(): void {
    this.initializeRouteFilters();
    this.subscribeToAccountChanges();
    this.initializeDateRangePicker();
    this.updateSupportDetailQueryParams();
    void this.loadDashboard();
  }

  // Render charts only after the view elements are available.
  ngAfterViewInit(): void {
    this.chartsReady = true;
    this.scheduleChartRender();
    window.addEventListener("resize", this.resizeCharts);
  }

  // Clean up timers, subscriptions, and chart instances.
  ngOnDestroy(): void {
    if (this.currentMessage) {
      this.currentMessage.unsubscribe();
    }

    if (this.chartRenderTimer) {
      clearTimeout(this.chartRenderTimer);
    }

    window.removeEventListener("resize", this.resizeCharts);
    this.disposeCharts();
  }

  // Template-friendly getters keep the HTML simple.
  get volumeData(): VolumeByPriorityRow[] {
    return this.volumeByPriorityTableState.responseBody?.summary?.rows || [];
  }

  get totalTickets(): number {
    return this.volumeByPriorityTableState.responseBody?.summary?.total_tickets || 0;
  }

  get slaOverallData(): SlaComplianceOverallRow[] {
    return this.slaComplianceOverallTableState.responseBody?.summary?.rows || [];
  }

  get resolutionSLA(): string {
    return this.getSlaComplianceDisplay("Resolution SLA");
  }

  get responseSLA(): string {
    return this.getSlaComplianceDisplay("Response SLA");
  }

  get slaPriorityData(): SlaComplianceByPriorityRow[] {
    return (
      this.slaComplianceByPriorityTableState.responseBody?.summary?.rows || []
    );
  }

  get slaPriorityTotal(): SlaComplianceByPriorityTotal {
    return (
      this.slaComplianceByPriorityTableState.responseBody?.summary?.total ||
      EMPTY_SLA_PRIORITY_TOTAL
    );
  }

  get ticketsByPriorityData(): TicketsByPriorityRow[] {
    return this.ticketsByPriorityTableState.responseBody?.summary?.daily_sunrise_report?.rows || [];
  }

  get mttrData(): MttrByPriorityRow[] {
    return this.mttrByPriorityTableState.responseBody?.summary?.rows || [];
  }

  get mttrOverall(): MttrByPriorityOverall {
    return this.mttrByPriorityTableState.responseBody?.summary?.overall || EMPTY_MTTR_OVERALL;
  }

  get avgMTTR(): string {
    const avgResolutionTime =
      this.mttrByPriorityTableState.responseBody?.summary?.overall
        ?.avg_resolution_time_hours;
    return avgResolutionTime == null ? "0" : avgResolutionTime.toString();
  }

  get creationTimeData(): TicketsByCreationTimeRow[] {
    return (
      this.ticketsByCreationTimeTableState.responseBody?.summary?.rows ||
      this.ticketsByPriorityTableState.responseBody?.summary
        ?.tickets_by_creation_time?.rows ||
      []
    );
  }

  get hasAssigneeFilter(): boolean {
    return Boolean(this.selectedAssignees.length);
  }

  get hasOrganizationFilter(): boolean {
    return Boolean(this.selectedOrganization.trim());
  }

  get allAssigneesSelected(): boolean {
    return !this.selectedAssignees.length;
  }

  get selectedAssigneeSummary(): string {
    if (!this.selectedAssignees.length) {
      return "All Assignees";
    }

    if (this.selectedAssignees.length === 1) {
      return this.selectedAssignees[0];
    }

    return `${this.selectedAssignees.length} assignees selected`;
  }

  get isDownloadDisabled(): boolean {
    return (
      this.loadingOrganizations ||
      this.loadingAssignees ||
      this.volumeByPriorityTableState.loading ||
      this.slaComplianceOverallTableState.loading ||
      this.slaComplianceByPriorityTableState.loading ||
      this.ticketsByPriorityTableState.loading ||
      this.mttrByPriorityTableState.loading ||
      this.ticketsByCreationTimeTableState.loading
    );
  }

  get downloadExportData(): SupportDashboardDownloadData {
    return {
      startDate: this.startDate,
      endDate: this.endDate,
      assigneeDisplay: this.getExportAssigneeDisplay(),
      totalTickets: this.totalTickets,
      resolutionSLA: this.resolutionSLA,
      responseSLA: this.responseSLA,
      avgMTTR: this.avgMTTR,
      volumeData: this.volumeData,
      slaOverallData: this.slaOverallData,
      slaPriorityData: this.slaPriorityData,
      slaPriorityTotal: this.slaPriorityTotal,
      mttrData: this.mttrData,
      mttrOverall: this.mttrOverall,
      creationTimeData: this.creationTimeData,
    };
  }

  readonly prepareDownloadCharts = async (): Promise<void> => {
    if (!this.chartsReady) {
      return;
    }

    this.isPreparingChartExport = true;

    try {
      this.renderCharts();
      this.resizeCharts();
      await this.waitForChartPaint();
    } finally {
      this.isPreparingChartExport = false;
    }
  };

  readonly getDownloadChartImageUrl = (chartKey: string): string | null => {
    const chart = this.charts[chartKey];
    if (!chart) {
      return null;
    }

    try {
      return chart.getDataURL({
        type: "png",
        pixelRatio: 2,
        backgroundColor: "#ffffff",
      });
    } catch {
      return null;
    }
  };
  
  getMetricRoute(metric: SupportDashboardDetailMetric): string[] {
    return ["/dash/support-dashboard", metric];
  }

  getSlaMetricRoute(
    slaType: string,
    status: "met" | "breached"
  ): string[] {
    const normalizedSlaType = (slaType || "").trim().toLowerCase();
    const metric =
      normalizedSlaType === "resolution sla"
        ? status === "met"
          ? "resolution-met"
          : "resolution-breached"
        : status === "met"
        ? "response-met"
        : "response-breached";

    return this.getMetricRoute(metric);
  }

  getCreationTimeMetricRoute(timePeriod: string): string[] {
    const metric = this.getCreationTimeMetric(timePeriod);
    return metric ? this.getMetricRoute(metric) : [];
  }

  isCreationTimeMetricLinkable(timePeriod: string): boolean {
    return Boolean(this.getCreationTimeMetric(timePeriod));
  }

  getPriorityMetricRoute(): string[] {
    return this.getMetricRoute("priority-details");
  }

  getSlaPriorityMetricRoute(): string[] {
    return this.getMetricRoute("sla-priority-details");
  }

  getPriorityDetailQueryParams(priority: string): {
    startDate: string;
    endDate: string;
    dateField: string;
    includeResolved: boolean;
    organization?: string;
    assignees?: string;
    priority?: string;
  } {
    return {
      ...this.supportDetailQueryParams,
      priority: (priority || "").trim() || undefined,
    };
  }

  getSlaPriorityDetailQueryParams(
    priority: string,
    slaName: "time_to_resolution" | "time_to_first_response",
    slaState: "met" | "breached"
  ): {
    startDate: string;
    endDate: string;
    dateField: string;
    includeResolved: boolean;
    organization?: string;
    assignees?: string;
    priority?: string;
    slaName: "time_to_resolution" | "time_to_first_response";
    slaState: "met" | "breached";
  } {
    return {
      ...this.supportDetailQueryParams,
      priority: (priority || "").trim() || undefined,
      slaName,
      slaState,
    };
  }

  async onOrganizationSelectionChange(
    selectedValue: OrganizationSelectionValue | null
  ): Promise<void> {
    const nextSelectedOrganization =
      this.normalizeOrganizationSelection(selectedValue);

    if (nextSelectedOrganization !== this.selectedOrganization) {
      this.selectedAssignees = [];
    }

    this.selectedOrganization = nextSelectedOrganization;
    this.updateSupportDetailQueryParams();
    await this.loadDashboard();
  }

  async onAssigneeSelectionChange(
    selectedValues: AssigneeSelectionValue[]
  ): Promise<void> {
    this.selectedAssignees = this.normalizeAssigneeSelection(selectedValues);
    this.updateSupportDetailQueryParams();
    await this.loadDashboard();
  }

  async selectAllAssignees(): Promise<void> {
    if (this.allAssigneesSelected) {
      return;
    }

    this.selectedAssignees = [];
    this.updateSupportDetailQueryParams();
    await this.loadDashboard();
  }

  // Load all dashboard sections for the selected account and date range.
  async loadDashboard(): Promise<void> {
    if (!this.isDateRangeValid()) {
      this.loadingSlaSummaryCards = false;
      return;
    }

    const jiraContext = this.getJiraContext();
    if (!jiraContext) {
      this.loadingSlaSummaryCards = false;
      this.organizationOptions = [];
      this.selectedOrganization = "";
      this.assigneeOptions = [];
      this.selectedAssignees = [];
      this.resetAllTableStates();
      this.scheduleChartRender();
      this.notifier.alert(
        "Info",
        "",
        "Managed Services support via SWAYAM is not enabled for your account. Please contact the admin.",
        "info",
        5000
      );
      return;
    }

    await Promise.all([
      this.loadOrganizationOptions(jiraContext),
      this.loadAssigneeOptions(jiraContext),
      this.loadVolumeByPriorityTable(jiraContext),
      this.loadSlaComplianceOverallTable(jiraContext),
      this.loadSlaComplianceByPriorityTable(jiraContext),
      this.loadTicketsByPriorityTable(jiraContext),
      // this.loadMttrByPriorityTable(jiraContext),
      this.loadTicketsByCreationTimeTable(jiraContext),
    ]);
    this.scheduleChartRender();
  }

  // Section loaders reuse the same API handling with different actions.
  async loadVolumeByPriorityTable(jiraContext: JiraContext): Promise<void> {
    const payload = this.buildRequestPayload("priority_summary", jiraContext);
    await this.loadTableState(
      this.volumeByPriorityTableState,
      payload,
      "Unable to load volume by priority."
    );
  }

  async loadSlaComplianceOverallTable(jiraContext: JiraContext): Promise<void> {
    const payload = this.buildRequestPayload("sla_summary", jiraContext);
    this.loadingSlaSummaryCards = true;

    try {
      await this.loadTableState(
        this.slaComplianceOverallTableState,
        payload,
        "Unable to load SLA compliance overall."
      );
    } finally {
      this.loadingSlaSummaryCards = false;
    }
  }

  async loadSlaComplianceByPriorityTable(
    jiraContext: JiraContext
  ): Promise<void> {
    const payload = this.buildRequestPayload("sla_priority_summary", jiraContext);
    await this.loadTableState(
      this.slaComplianceByPriorityTableState,
      payload,
      "Unable to load SLA compliance by priority."
    );
  }

  async loadTicketsByPriorityTable(jiraContext: JiraContext): Promise<void> {
    const payload = this.buildRequestPayload("operational_summary", jiraContext);
    await this.loadTableState(
      this.ticketsByPriorityTableState,
      payload,
      "Unable to load tickets by priority."
    );
  }

  // async loadMttrByPriorityTable(jiraContext: JiraContext): Promise<void> {
  //   const payload = this.buildRequestPayload("mttr_summary", jiraContext);
  //   await this.loadTableState(
  //     this.mttrByPriorityTableState,
  //     payload,
  //     "Unable to load MTTR by priority."
  //   );
  // }

  async loadTicketsByCreationTimeTable(
    jiraContext: JiraContext
  ): Promise<void> {
    const payload = this.buildRequestPayload(
      "creation_time_summary",
      jiraContext
    );
    await this.loadTableState(
      this.ticketsByCreationTimeTableState,
      payload,
      "Unable to load tickets by creation time."
    );
  }

  private getExportAssigneeDisplay(): string {
    if (!this.hasAssigneeFilter) {
      return "All Assignees";
    }

    return this.selectedAssignees.join(", ");
  }

  // Used by the summary cards to pick one SLA percentage from the overall list.
  private getSlaComplianceDisplay(slaType: string): string {
    const slaRow = this.slaOverallData.find((row) => {
      return row.sla_type?.toLowerCase() === slaType.toLowerCase();
    });

    return slaRow?.compliance_percentage?.display || "0.00%";
  }

  // Initialize the flatpickr date range and reload on a completed range selection.
  private initializeDateRangePicker(): void {
    this.syncDateRangeDisplay();

    setTimeout(() => {
      flatpickr(".datetimepicker", {
        dateFormat: "d-M-Y",
        mode: "range",
        maxDate: new Date(),
        showMonths: 2,
        onChange: async (
          selectedDates: Date[],
          dateStr: string,
          instance: any
        ) => {
          if (!dateStr.includes("to")) {
            return;
          }

          this.dateRange = dateStr;
          this.updateDateRangeValues(dateStr);
          await this.loadDashboard();
        },
      });
    }, 100);
  }

  // Keep the visible date range text aligned with the API date values.
  private syncDateRangeDisplay(): void {
    this.dateRange =
      moment(this.startDate, "YYYY-MM-DD").format("DD-MMM-YYYY") +
      " to " +
      moment(this.endDate, "YYYY-MM-DD").format("DD-MMM-YYYY");
  }

  private updateDateRangeValues(dateRange: string): void {
    if (!(dateRange && dateRange.includes("to"))) {
      return;
    }

    const selectedDateRange = dateRange.toString().split(" to ");
    this.startDate = moment(selectedDateRange[0], "DD-MMM-YYYY").format(
      "YYYY-MM-DD"
    );
    this.endDate = moment(selectedDateRange[1], "DD-MMM-YYYY").format(
      "YYYY-MM-DD"
    );
    this.updateSupportDetailQueryParams();
  }

  private initializeRouteFilters(): void {
    const queryParams = this.route.snapshot.queryParamMap;
    const startDate = queryParams.get("startDate");
    const endDate = queryParams.get("endDate");
    const dateField = queryParams.get("dateField");
    const includeResolved = queryParams.get("includeResolved");
    const organization = queryParams.get("organization");
    const assignees = queryParams.get("assignees") || queryParams.get("reporters");

    if (this.isValidQueryDate(startDate)) {
      this.startDate = startDate;
    }

    if (this.isValidQueryDate(endDate)) {
      this.endDate = endDate;
    }

    if (dateField) {
      this.dateField = dateField;
    }

    if (includeResolved != null) {
      this.includeResolved = includeResolved === "true";
    }

    this.selectedOrganization = this.normalizeOrganizationSelection(organization);
    this.selectedAssignees = this.parseAssigneeQueryValue(assignees);
  }

  private isValidQueryDate(date: string | null): date is string {
    return Boolean(date) && moment(date, "YYYY-MM-DD", true).isValid();
  }

  private updateSupportDetailQueryParams(): void {
    this.supportDetailQueryParams = {
      startDate: this.startDate,
      endDate: this.endDate,
      dateField: this.dateField,
      includeResolved: this.includeResolved,
      organization: this.hasOrganizationFilter
        ? this.selectedOrganization.trim()
        : undefined,
      assignees: this.hasAssigneeFilter
        ? JSON.stringify(this.selectedAssignees)
        : undefined,
    };
  }

  // Refresh the dashboard when the selected account changes elsewhere in the app.
  private subscribeToAccountChanges(): void {
    let ignoreInitialReplay = true;

    this.currentMessage = this.notifier.currentMessage.subscribe((msg) => {
      if (ignoreInitialReplay) {
        ignoreInitialReplay = false;
        return;
      }

      if (!msg) {
        return;
      }

      let notificationData: any;
      try {
        notificationData = JSON.parse(msg);
      } catch {
        return;
      }

      if (
        notificationData?.value == null ||
        notificationData?.source !== "notifier"
      ) {
        return;
      }

      if (notificationData.key === "accountId") {
        this.accountId = notificationData.value;
        this.selectedOrganization = "";
        this.organizationOptions = [];
        this.selectedAssignees = [];
        this.assigneeOptions = [];
        this.updateSupportDetailQueryParams();
        void this.loadDashboard();
      }
    });
  }

  // Build the request payload expected by the support dashboard API.
  private buildRequestPayload(
    action: SupportDashboardAction,
    jiraContext: JiraContext
  ): SupportDashboardRequestPayload {
    const payload: SupportDashboardRequestPayload = {
      action,
      jira_project_key: jiraContext.jiraProjectKeys,
      start_date: this.startDate,
      end_date: this.endDate,
      date_field: this.dateField,
      include_resolved: this.includeResolved,
    };

    if (this.hasOrganizationFilter) {
      payload.jira_organization_key = this.selectedOrganization.trim();
    }

    if (this.hasAssigneeFilter) {
      payload.assignees = [...this.selectedAssignees];
    }

    return payload;
  }

  private getCreationTimeMetric(
    timePeriod: string
  ): SupportDashboardDetailMetric | null {
    const normalizedTimePeriod = (timePeriod || "")
      .trim()
      .toLowerCase()
      .replace(/[^a-z]+/g, "_");

    if (
      normalizedTimePeriod.includes("non_business") ||
      normalizedTimePeriod.includes("after_hours") ||
      normalizedTimePeriod.includes("after")
    ) {
      return "after-hours";
    }

    if (normalizedTimePeriod.includes("business")) {
      return "business-hours";
    }

    return null;
  }

  // Read Jira keys for the currently selected account from cached account data.
  private getJiraContext(): JiraContext | null {
    const selectedAccount = this.fetchSelectedAccountDetails();
    const jiraOrganizationKey = selectedAccount?.jira_org_name?.trim();
    const jiraProjectKey = selectedAccount?.jira_project_key?.trim();
    const jiraProjectKeys = this.normalizeProjectKeys(jiraProjectKey);

    if (!jiraProjectKey || !jiraProjectKeys.length) {
      return null;
    }

    return {
      jiraOrganizationKey: jiraOrganizationKey || "",
      jiraProjectKey,
      jiraProjectKeys,
    };
  }

  // Find the selected account details from local storage.
  private fetchSelectedAccountDetails(): any | null {
    const selectedAccounts = localStorage.getItem("accountIdData");
    if (!selectedAccounts || !this.accountId) {
      return null;
    }

    try {
      const parsedAccounts = JSON.parse(selectedAccounts);
      return (
        parsedAccounts?.list?.find((account: any) => {
          return account.accountId == this.accountId;
        }) || null
      );
    } catch {
      return null;
    }
  }

  private normalizeProjectKeys(jiraProjectKey: string): string[] {
    if (!jiraProjectKey) {
      return [];
    }

    const trimmedProjectKey = jiraProjectKey.trim();

    if (trimmedProjectKey.startsWith("[")) {
      try {
        const parsedValue = JSON.parse(trimmedProjectKey);
        if (Array.isArray(parsedValue)) {
          return parsedValue
            .map((value) => String(value).trim())
            .filter((value) => value);
        }
      } catch {
        return [trimmedProjectKey];
      }
    }

    if (trimmedProjectKey.includes(",")) {
      return trimmedProjectKey
        .split(",")
        .map((value) => value.trim())
        .filter((value) => value);
    }

    return [trimmedProjectKey];
  }

  private async loadOrganizationOptions(jiraContext: JiraContext): Promise<void> {
    this.loadingOrganizations = true;

    try {
      const result = await this.apiService.postDataPromis(
        this.getSupportOrganizationApiUrl(jiraContext),
        {},
        this.getRequestHeaders()
      );
      const response = this.normalizeOrganizationResponse(result);

      if (this.isSuccessResponse(response)) {
        this.organizationOptions = (response?.organizations || [])
          .map((organization): OrganizationOption | null => {
            const organizationName = (
              organization?.organization_name ||
              organization?.name ||
              ""
            ).trim();

            if (!organizationName) {
              return null;
            }

            return {
              id: organizationName,
              name: organizationName,
              ticketCount: Number(organization?.ticket_count) || 0,
            };
          })
          .filter(
            (
              organization
            ): organization is OrganizationOption => organization !== null
          );
      } else {
        this.organizationOptions = [];
      }
    } catch {
      this.organizationOptions = [];
    } finally {
      if (
        this.selectedOrganization &&
        !this.organizationOptions.some(
          (option: OrganizationOption) => option.id === this.selectedOrganization
        )
      ) {
        this.selectedOrganization = "";
        this.selectedAssignees = [];
      }

      this.updateSupportDetailQueryParams();
      this.loadingOrganizations = false;
    }
  }

  private async loadAssigneeOptions(jiraContext: JiraContext): Promise<void> {
    this.loadingAssignees = true;

    try {
      const result = await this.apiService.postDataPromis(
        this.getSupportAssigneeApiUrl(jiraContext),
        {},
        this.getRequestHeaders()
      );
      const response = this.normalizeAssigneeResponse(result);

      if (this.isSuccessResponse(response)) {
        const rawAssignees =
          (response as any)?.assignees || (response as any)?.reporters || [];
        const mappedOptions = (rawAssignees || [])
          .map((assignee: any): AssigneeOption | null => {
            const assigneeName = (
              assignee?.assignee_name ||
              assignee?.reporter_name ||
              assignee?.name ||
              ""
            ).trim();
            if (!assigneeName) {
              return null;
            }

            return {
              id: assigneeName,
              name: assigneeName,
              ticketCount: Number(assignee?.ticket_count) || 0,
            };
          })
          .filter(
            (assignee: AssigneeOption | null): assignee is AssigneeOption =>
              assignee !== null
          );

        this.assigneeOptions = mappedOptions;
      } else {
        this.assigneeOptions = [];
      }
    } catch {
      this.assigneeOptions = [];
    } finally {
      this.selectedAssignees = this.normalizeAssigneeSelection(
        this.selectedAssignees.filter((assigneeName) => {
          return this.assigneeOptions.some(
            (option: AssigneeOption) => option.id === assigneeName
          );
        })
      );
      this.updateSupportDetailQueryParams();
      this.loadingAssignees = false;
    }
  }

  // Shared loader for all table sections: set state, call API, normalize response.
  private async loadTableState<T extends DashboardResponseBase>(
    state: DashboardTableState<T>,
    payload: SupportDashboardRequestPayload,
    fallbackErrorMessage: string
  ): Promise<void> {
    state.loading = true;
    state.errorMessage = null;
    state.requestPayload = payload;

      try {
        const result = await this.apiService.postDataPromis(
          this.getSupportDashboardApiUrl(payload),
          payload,
          this.getRequestHeaders()
        );
      const response = this.normalizeDashboardResponse<T>(result);

      if (response && (response.status == "1" || (response as any).s == "1")) {
        state.responseBody = response as T;
      } else {
        state.responseBody = null;
        state.errorMessage =
          response?.error_message || response?.message || fallbackErrorMessage;
      }
    } catch (error) {
      state.responseBody = null;
      state.errorMessage = this.getErrorMessage(error, fallbackErrorMessage);
    } finally {
      state.loading = false;
      this.scheduleChartRender();
    }
  }

  // Build the final endpoint URL with query parameters for the current request.
  private getSupportDashboardApiUrl(
    payload: SupportDashboardRequestPayload
  ): string {
    const queryString = this.buildQueryString(payload);
    return `${environment.apiURL}/${this.urlPrefix}/apis/support/jira?${queryString}`;
  }

  private getSupportAssigneeApiUrl(jiraContext: JiraContext): string {
    const params = new URLSearchParams();

    params.set("action", "list_assignees_by_organization");
    params.set("jira_project_key", JSON.stringify(jiraContext.jiraProjectKeys));
    params.set("start_date", this.startDate);
    params.set("end_date", this.endDate);
    params.set("date_field", this.dateField);
    params.set("include_resolved", String(this.includeResolved));

    if (this.hasOrganizationFilter) {
      params.set("jira_organization_key", this.selectedOrganization.trim());
    }

    return `${environment.apiURL}/${this.urlPrefix}/apis/support/jira?${params.toString()}`;
  }

  private getSupportOrganizationApiUrl(jiraContext: JiraContext): string {
    const params = new URLSearchParams();

    params.set("action", "list_organizations_by_project_key");
    params.set("jira_project_key", JSON.stringify(jiraContext.jiraProjectKeys));
    params.set("start_date", this.startDate);
    params.set("end_date", this.endDate);
    params.set("date_field", this.dateField);
    params.set("include_resolved", String(this.includeResolved));

    return `${environment.apiURL}/${this.urlPrefix}/apis/support/jira?${params.toString()}`;
  }

  // Convert the request payload into a query string understood by the API.
  private buildQueryString(payload: SupportDashboardRequestPayload): string {
    const queryParts = [
      `action=${encodeURIComponent(payload.action)}`,
      `jira_project_key=${encodeURIComponent(
        JSON.stringify(payload.jira_project_key)
      )}`,
      `start_date=${encodeURIComponent(payload.start_date)}`,
      `end_date=${encodeURIComponent(payload.end_date)}`,
    ];

    if (payload.jira_organization_key?.trim()) {
      queryParts.push(
        `jira_organization_key=${encodeURIComponent(
          payload.jira_organization_key
        )}`
      );
    }

    if (payload.assignees?.length) {
      queryParts.push(
        `assignees=${encodeURIComponent(JSON.stringify(payload.assignees))}`
      );
    }

    queryParts.push(
      `include_resolved=${encodeURIComponent(String(payload.include_resolved))}`
    );
    queryParts.push(`date_field=${encodeURIComponent(payload.date_field)}`);

    return queryParts.join("&");
  }

  private getRequestHeaders(): { Authorization: string | null } {
    return {
      Authorization: localStorage.getItem("t"),
    };
  }

  // Handle the API's different response shapes in one place.
  private normalizeDashboardResponse<T extends DashboardResponseBase>(
    result: any
  ): (T & { error_message?: string; message?: string; s?: string }) | null {
    if (result == null) {
      return null;
    }

    let response = result?.body !== undefined ? result.body : result;
    if (typeof response === "string") {
      try {
        response = JSON.parse(response);
      } catch {
        return {
          status: "0",
          message: response,
        } as T & { message: string };
      }
    }

    return response as T & { error_message?: string; message?: string; s?: string };
  }

  private normalizeAssigneeResponse(
    result: any
  ): AssigneeListResponse | null {
    if (result == null) {
      return null;
    }

    let response = result?.body !== undefined ? result.body : result;
    if (typeof response === "string") {
      try {
        response = JSON.parse(response);
      } catch {
        return null;
      }
    }

    return response as AssigneeListResponse;
  }

  private normalizeOrganizationResponse(
    result: any
  ): OrganizationListResponse | null {
    if (result == null) {
      return null;
    }

    let response = result?.body !== undefined ? result.body : result;
    if (typeof response === "string") {
      try {
        response = JSON.parse(response);
      } catch {
        return null;
      }
    }

    return response as OrganizationListResponse;
  }

  private isSuccessResponse(
    response: { status?: string | number; s?: string | number } | null
  ): boolean {
    return Boolean(response) && (response?.status == "1" || response?.s == "1");
  }

  private normalizeAssigneeSelection(
    assignees: AssigneeSelectionValue[]
  ): string[] {
    const normalizedAssignees = Array.from(
      new Set(
        (assignees || [])
          .map((assignee) => {
            if (typeof assignee === "string") {
              return assignee.trim();
            }

            if (assignee && typeof assignee === "object") {
              if (typeof assignee.id === "string" && assignee.id.trim()) {
                return assignee.id.trim();
              }

              if (typeof assignee.name === "string" && assignee.name.trim()) {
                return assignee.name.trim();
              }
            }

            return "";
          })
          .filter((assignee) => assignee)
      )
    );

    return normalizedAssignees;
  }

  private normalizeOrganizationSelection(
    organization: OrganizationSelectionValue | string | null | undefined
  ): string {
    if (typeof organization === "string") {
      return organization.trim();
    }

    if (organization && typeof organization === "object") {
      if (typeof organization.id === "string" && organization.id.trim()) {
        return organization.id.trim();
      }

      if (typeof organization.name === "string" && organization.name.trim()) {
        return organization.name.trim();
      }
    }

    return "";
  }

  private parseAssigneeQueryValue(assignees: string | null): string[] {
    if (!assignees) {
      return [];
    }

    try {
      const parsedValue = JSON.parse(assignees);
      if (Array.isArray(parsedValue)) {
        return this.normalizeAssigneeSelection(parsedValue);
      }
    } catch {
      return this.normalizeAssigneeSelection(
        assignees
          .split(",")
          .map((value) => value.trim())
          .filter((value) => value)
      );
    }

    return [];
  }

  // Reset all sections when support data is unavailable for the selected account.
  private resetAllTableStates(): void {
    [
      this.volumeByPriorityTableState,
      this.slaComplianceOverallTableState,
      this.slaComplianceByPriorityTableState,
      this.ticketsByPriorityTableState,
      this.mttrByPriorityTableState,
      this.ticketsByCreationTimeTableState,
    ].forEach((state) => {
      state.loading = false;
      state.errorMessage = null;
      state.requestPayload = null;
      state.responseBody = null;
    });
  }

  // Stop API calls early when the selected date range is invalid.
  private isDateRangeValid(): boolean {
    if (!this.startDate || !this.endDate) {
      this.notifier.alert(
        "Info",
        "",
        "Please select both start date and end date.",
        "info",
        5000
      );
      return false;
    }

    if (this.startDate > this.endDate) {
      this.notifier.alert(
        "Info",
        "",
        "Start date should be less than or equal to end date.",
        "info",
        5000
      );
      return false;
    }

    return true;
  }

  private getErrorMessage(error: unknown, fallbackMessage: string): string {
    if (typeof error === "string" && error.trim()) {
      return error;
    }

    if (
      error &&
      typeof error === "object" &&
      "message" in error &&
      typeof (error as { message: string }).message === "string"
    ) {
      return (error as { message: string }).message;
    }

    return fallbackMessage;
  }

  // Delay chart rendering slightly so the view updates first.
  private scheduleChartRender(): void {
    if (!this.chartsReady) {
      return;
    }

    if (this.chartRenderTimer) {
      clearTimeout(this.chartRenderTimer);
    }

    this.chartRenderTimer = setTimeout(() => {
      this.chartRenderTimer = null;
      this.renderCharts();
      this.resizeCharts();
    }, 0);
  }

  private waitForChartPaint(frameCount = 2): Promise<void> {
    return new Promise((resolve) => {
      const requestFrame =
        typeof window.requestAnimationFrame === "function"
          ? window.requestAnimationFrame.bind(window)
          : (callback: FrameRequestCallback) =>
              window.setTimeout(() => callback(Date.now()), 16);

      const paintFrame = () => {
        if (frameCount <= 0) {
          resolve();
          return;
        }

        frameCount -= 1;
        requestFrame(paintFrame);
      };

      requestFrame(paintFrame);
    });
  }

  // Render every chart from the latest table data.
  private renderCharts(): void {
    this.renderVolumeByPriorityChart();
    this.renderSlaComplianceOverallChart();
    this.renderSlaComplianceByPriorityChart();
    this.renderMttrByPriorityChart();
    this.renderTicketsByCreationTimeChart();
  }

  // Donut chart showing ticket share by priority.
  private renderVolumeByPriorityChart(): void {
    const rows = this.volumeData.filter((row) => row.count > 0);
    const data = rows.map((row, index) => {
      const priority = row.priority || "Unknown";
      return {
        name: priority,
        value: this.getRoundedPercentage(row.percentage),
        ticketCount: row.count,
        itemStyle: {
          color: this.getPriorityColor(priority, index),
        },
      };
    });

    if (!this.hasPieData(data)) {
      this.setNoDataChart(
        "volumeByPriority",
        this.volumeByPriorityChart,
        "Volume by Priority"
      );
      return;
    }

    this.setChartOption("volumeByPriority", this.volumeByPriorityChart, {
      title: this.getChartTitle("Volume by Priority"),
      color: this.defaultChartColors,
      tooltip: {
        trigger: "item",
        formatter: (params: any) => {
          const point = params.data || {};
          return `${params.name}<br/>${point.value}% (${point.ticketCount} tickets)`;
        },
      },
      legend: {
        bottom: 0,
        left: "center",
      },
      series: [
        {
          name: "Priority",
          type: "pie",
          radius: ["38%", "68%"],
          center: ["50%", "48%"],
          avoidLabelOverlap: true,
          itemStyle: {
            borderColor: "#fff",
            borderWidth: 2,
          },
          label: {
            formatter: (params: any) => `${params.name}: ${params.value}%`,
          },
          data,
        },
      ],
    });
  }

  // Horizontal bar chart showing met vs breached counts per SLA type.
  private renderSlaComplianceOverallChart(): void {
    const rows = this.slaOverallData;
    const categories = rows.map((row) => row.sla_type);
    const metData = rows.map((row) => row.met);
    const breachedData = rows.map((row) => row.breached);

    if (!rows.length) {
      this.setNoDataChart(
        "slaComplianceOverall",
        this.slaComplianceOverallChart,
        "SLA Compliance - Overall"
      );
      return;
    }

    this.setChartOption("slaComplianceOverall", this.slaComplianceOverallChart, {
      title: this.getChartTitle("SLA Compliance - Overall"),
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "shadow",
        },
      },
      legend: {
        bottom: 0,
      },
      grid: this.getHorizontalBarGrid(),
      xAxis: {
        type: "value",
        splitLine: this.getSplitLineStyle(),
      },
      yAxis: {
        type: "category",
        data: categories,
      },
      series: [
        {
          name: "Met",
          type: "bar",
          data: metData,
          itemStyle: {
            color: this.metColor,
          },
          label: this.getNumberBarLabel(),
        },
        {
          name: "Breached",
          type: "bar",
          data: breachedData,
          itemStyle: {
            color: this.breachedColor,
          },
          label: this.getNumberBarLabel(),
        },
      ],
    });
  }

  // Horizontal bar chart comparing response and resolution SLA percentages.
  private renderSlaComplianceByPriorityChart(): void {
    const rows = this.sortPriorityRows(this.slaPriorityData);
    const categories = rows.map((row) => row.priority);
    const resolutionData = rows.map((row) => {
      return this.getPercentageDataItem(row.resolution_percentage);
    });
    const responseData = rows.map((row) => {
      return this.getPercentageDataItem(row.response_percentage);
    });

    if (!rows.length) {
      this.setNoDataChart(
        "slaComplianceByPriority",
        this.slaComplianceByPriorityChart,
        "SLA Compliance by Priority"
      );
      return;
    }

    this.setChartOption(
      "slaComplianceByPriority",
      this.slaComplianceByPriorityChart,
      {
        title: this.getChartTitle("SLA Compliance by Priority"),
        tooltip: {
          trigger: "axis",
          axisPointer: {
            type: "shadow",
          },
          formatter: (params: any) => {
            return this.formatPercentageTooltip(params);
          },
        },
        legend: {
          bottom: 0,
        },
        grid: this.getHorizontalBarGrid(),
        xAxis: {
          type: "value",
          max: 100,
          axisLabel: {
            formatter: "{value}%",
          },
          splitLine: this.getSplitLineStyle(),
        },
        yAxis: {
          type: "category",
          data: categories,
        },
        series: [
          {
            name: "Resolution SLA %",
            type: "bar",
            barMaxWidth: 18,
            data: resolutionData,
            itemStyle: {
              color: this.resolutionColor,
            },
            label: this.getPercentageBarLabel(),
          },
          {
            name: "Response SLA %",
            type: "bar",
            barMaxWidth: 18,
            data: responseData,
            itemStyle: {
              color: this.responseColor,
            },
            label: this.getPercentageBarLabel(),
          },
        ],
      }
    );
  }

  // Horizontal bar chart showing average resolution time by priority.
  private renderMttrByPriorityChart(): void {
    const rows = this.sortPriorityRows(this.mttrData);
    const categories = rows.map((row) => row.priority);
    const data = rows.map((row, index) => {
      const priority = row.priority || "Unknown";
      return {
        value: this.toNumber(row.avg_resolution_time_hours),
        itemStyle: {
          color: this.getPriorityColor(priority, index),
        },
      };
    });

    if (!rows.length) {
      this.setNoDataChart(
        "mttrByPriority",
        this.mttrByPriorityChart,
        "MTTR by Priority"
      );
      return;
    }

    this.setChartOption("mttrByPriority", this.mttrByPriorityChart, {
      title: this.getChartTitle("MTTR by Priority"),
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "shadow",
        },
        formatter: (params: any) => {
          const firstPoint = Array.isArray(params) ? params[0] : params;
          return `${firstPoint.name}<br/>Average Resolution Time: ${this.formatNumber(
            firstPoint.value
          )} hrs`;
        },
      },
      grid: this.getHorizontalBarGrid(110),
      xAxis: {
        type: "value",
        name: "Hours",
        splitLine: this.getSplitLineStyle(),
      },
      yAxis: {
        type: "category",
        data: categories,
      },
      series: [
        {
          name: "Avg Resolution Time",
          type: "bar",
          data,
          label: {
            show: true,
            position: "right",
            formatter: (params: any) => `${this.formatNumber(params.value)} hrs`,
          },
        },
      ],
    });
  }

  // Donut chart showing ticket distribution by creation time buckets.
  private renderTicketsByCreationTimeChart(): void {
    const rows = this.creationTimeData.filter((row) => {
      return row.ticket_count > 0 && !this.isTotalLabel(row.time_period);
    });
    const data = rows.map((row, index) => {
      return {
        name: row.time_period,
        value: this.getRoundedPercentage(row.percentage),
        ticketCount: row.ticket_count,
        itemStyle: {
          color: this.defaultChartColors[index % this.defaultChartColors.length],
        },
      };
    });

    if (!this.hasPieData(data)) {
      this.setNoDataChart(
        "ticketsByCreationTime",
        this.ticketsByCreationTimeChart,
        "Tickets by Creation Time"
      );
      return;
    }

    this.setChartOption(
      "ticketsByCreationTime",
      this.ticketsByCreationTimeChart,
      {
        title: this.getChartTitle("Tickets by Creation Time"),
        color: this.defaultChartColors,
        tooltip: {
          trigger: "item",
          formatter: (params: any) => {
            const point = params.data || {};
            return `${params.name}<br/>${point.value}% (${point.ticketCount} tickets)`;
          },
        },
        legend: {
          bottom: 0,
          left: "center",
        },
        series: [
          {
            name: "Creation Time",
            type: "pie",
            radius: ["38%", "68%"],
            center: ["50%", "48%"],
            avoidLabelOverlap: true,
            itemStyle: {
              borderColor: "#fff",
              borderWidth: 2,
            },
            label: {
              formatter: (params: any) => `${params.name}: ${params.value}%`,
            },
            data,
          },
        ],
      }
    );
  }

  // Reuse or create the chart instance before applying the latest option.
  private setChartOption(
    key: string,
    chartElement: ElementRef<HTMLDivElement>,
    option: echarts.EChartsOption
  ): void {
    if (!chartElement?.nativeElement) {
      return;
    }

    const chartDom = chartElement.nativeElement;
    const chart =
      this.charts[key] || echarts.getInstanceByDom(chartDom) || echarts.init(chartDom);
    this.charts[key] = chart;
    const chartOption = this.isPreparingChartExport
      ? ({ ...option, animation: false } as echarts.EChartsOption)
      : option;
    chart.setOption(chartOption, true);
  }

  // Fallback chart state when a section has no data to plot.
  private setNoDataChart(
    key: string,
    chartElement: ElementRef<HTMLDivElement>,
    title: string
  ): void {
    this.setChartOption(key, chartElement, {
      title: this.getChartTitle(title),
      graphic: {
        type: "text",
        left: "center",
        top: "middle",
        style: {
          text: "No data found",
          fill: "#777",
          fontSize: 14,
          fontWeight: 600,
        },
      },
      series: [],
    });
  }

  // Dispose chart instances when leaving the page.
  private disposeCharts(): void {
    Object.values(this.charts).forEach((chart) => {
      chart.dispose();
    });
    this.charts = {};
  }

  private resizeCharts = (): void => {
    Object.values(this.charts).forEach((chart) => {
      chart.resize();
    });
  };

  // Shared title style for all charts.
  private getChartTitle(text: string): echarts.TitleComponentOption {
    return {
      text,
      left: "center",
      top: 0,
      textStyle: {
        color: "#333",
        fontSize: 14,
        fontWeight: 500,
      },
    };
  }

  // Shared grid spacing for horizontal bar charts.
  private getHorizontalBarGrid(left = 120): echarts.GridComponentOption {
    return {
      left,
      right: 45,
      top: 45,
      bottom: 55,
      containLabel: true,
    };
  }

  // Shared split-line styling to keep charts visually consistent.
  private getSplitLineStyle(): echarts.XAXisComponentOption["splitLine"] {
    return {
      lineStyle: {
        color: "#e6eaf2",
      },
    };
  }

  private getNumberBarLabel(): echarts.BarSeriesOption["label"] {
    return {
      show: true,
      position: "right",
      formatter: (params: any) => this.formatNumber(params.value),
    };
  }

  // Label formatter for bars that represent percentages.
  private getPercentageBarLabel(): echarts.BarSeriesOption["label"] {
    return {
      show: true,
      position: "right",
      formatter: (params: any) =>
        params.data?.percentageDisplay || `${params.value}%`,
    };
  }

  private getPercentageDataItem(percentage: PercentageDisplay): {
    value: number;
    percentageDisplay: string;
  } {
    const value = this.getPercentageValue(percentage);
    return {
      value,
      percentageDisplay: percentage?.display || `${this.formatNumber(value)}%`,
    };
  }

  private formatPercentageTooltip(params: any): string {
    const points = Array.isArray(params) ? params : [params];
    if (!points.length) {
      return "";
    }

    return [
      points[0].axisValue,
      ...points.map((point: any) => {
        return `${point.marker}${point.seriesName}: ${
          point.data?.percentageDisplay || `${point.value}%`
        }`;
      }),
    ].join("<br/>");
  }

  // Convert API percentage objects into safe numeric values for charts.
  private getRoundedPercentage(percentage: PercentageDisplay): number {
    return Math.round(this.getPercentageValue(percentage));
  }

  private getPercentageValue(percentage: PercentageDisplay): number {
    const parsedDisplay = Number.parseFloat(
      (percentage?.display || "").replace("%", "")
    );
    if (Number.isFinite(parsedDisplay)) {
      return this.clampPercentage(parsedDisplay);
    }

    if (percentage?.value != null && Number.isFinite(Number(percentage.value))) {
      const value = Number(percentage.value);
      return this.clampPercentage(value <= 1 ? value * 100 : value);
    }

    return 0;
  }

  private clampPercentage(value: number): number {
    return Math.min(Math.max(value, 0), 100);
  }

  private hasPieData(data: Array<{ value: number }>): boolean {
    return data.some((point) => point.value > 0);
  }

  // Sort priorities in business order instead of alphabetical order.
  private sortPriorityRows<T extends { priority: string }>(rows: T[]): T[] {
    const priorityOrder: { [key: string]: number } = {
      critical: 0,
      high: 1,
      medium: 2,
      low: 3,
    };

    return [...rows].sort((a, b) => {
      const aOrder = priorityOrder[a.priority?.toLowerCase()] ?? 99;
      const bOrder = priorityOrder[b.priority?.toLowerCase()] ?? 99;
      return aOrder - bOrder;
    });
  }

  // Pick a known priority color and fall back to the default palette.
  private getPriorityColor(priority: string, index: number): string {
    return (
      this.priorityColors[priority?.toLowerCase()] ||
      this.defaultChartColors[index % this.defaultChartColors.length]
    );
  }

  private isTotalLabel(label: string): boolean {
    const normalizedLabel = (label || "").toLowerCase();
    return (
      normalizedLabel === "total" ||
      normalizedLabel === "overall" ||
      normalizedLabel.includes("total")
    );
  }

  // Small number helpers used by labels and tooltips.
  private toNumber(value: number | null): number {
    const numberValue = Number(value ?? 0);
    return Number.isFinite(numberValue) ? numberValue : 0;
  }

  private formatNumber(value: number): string {
    const numberValue = this.toNumber(value);
    return numberValue.toLocaleString("en-US", {
      maximumFractionDigits: 2,
    });
  }
}
